import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { libraryLink, type FilterLink } from '@chess/api-insights/charts';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import { HELP_API, type Help } from '@chess/api-help';
import { registerCard } from '@chess/api-dashboard';
import '@chess/api-insights/charts.css';
import { MIDDLEGAME_TURNS_CARD, MiddlegameTurnsCard } from './MiddlegameTurnsCard.tsx';
import { MiddlegameTurnsAggregator } from './MiddlegameTurnsAggregator.ts';
import { MIDDLEGAME_TURNS_HELP, middlegameTurnsHelp } from './help.tsx';

// Middlegame card "When it turns": registers its own aggregator with the game
// store and its help topic, reads the result and renders
// `MiddlegameTurnsCard` first in the Middlegame screen's second row
// (`middlegame.why`, order 10; Home does not show it). A column opens the
// library filtered to its games (`middlegame-turn`, `libraryLink`).
export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  const aggregator = new MiddlegameTurnsAggregator();
  registerAggregator(getApi, aggregator);
  void app.getApi<Help>(HELP_API).then((help) => help.register(MIDDLEGAME_TURNS_HELP, middlegameTurnsHelp));
  const Card: React.FC = () => {
    const { result, state } = useAggregate(getApi, aggregator);
    const navigate = useNavigate();
    const link = React.useMemo<FilterLink>(() => libraryLink((path) => navigate(path)), [navigate]);
    // Never hidden while the store loads: the card shows skeletons for its values.
    if (state?.status !== 'ready') return state?.status === 'failed' ? null : <MiddlegameTurnsCard loading />;
    return result ? <MiddlegameTurnsCard result={result} link={link} /> : null;
  };
  registerCard(app, 'middlegame.why', Card, { order: 10, ...MIDDLEGAME_TURNS_CARD });
}
